import { useEffect } from 'react'
import { api } from '@api'
import { useQuery } from '@tanstack/react-query'
import { Profession } from './types'
import { Scope } from '@utils'
import { Moment } from './useGetMoments'
import { Hashtag } from './usePaginateHashtags'
import { Category } from './usePaginateCategories'
import { useLanguageStore } from '@store'
import { AvailableLanguages, resolveLanguage, useLanguage } from '@context'

export interface DataItem {
  code: string
  nameEn: string
  nameFr: string
  nameEs?: string
  /** Localized label for the current `lang` query — falls back to address, then ''. Always non-null. */
  name: string
  level?: string
}


export interface DayTimeSlot {
  days: number[]
  from: string
  to: string
}

export const PreferredMomentKeys = Object.freeze({
  aperitif: 'aperitif',
  breakfast: 'breakfast',
  coffee: 'coffee',
  dinner: 'dinner',
  lunch: 'lunch',
  walk: 'walk',
  video_call: 'video_call',
  phone_call: 'phone_call',
  weekend: 'weekend',
  sport: 'sport',
  event: 'event',
});

export interface Availability {
  moments: Moment[]
  slots: DayTimeSlot[]
}

export interface Profile {
  id: string
  accountId: string
  firstName: string
  lastName: string
  fullName: string
  avatarUrl: string
  bio: string
  birthDate: string
  gender: string
  hashtags: string[]
  pinsHashtags: Hashtag[]
  pinsCategories: Category[]
  goals: string[]
  hobbies: string[]
  skills: string[]
  contacts: {
    email?: string
    phone?: string
    website?: string
    facebook?: string
    instagram?: string
    linkedin?: string
    twitter?: string
  }
  scope?: Scope
  isGhost?: boolean
  professions: Profession[]
  residence: DataItem
  residenceHistory: DataItem[]
  origins: DataItem[]
  languages: DataItem[]
  nationalities: DataItem[]
  availabilities: Availability
  language?: string
  createdAt: string
  updatedAt: string
  /** Color labels (relational colors) this profile is in, for the current user */
  relationalColors?: { id: string; name: string; color: string }[]
  /** Lieu de référence (encounter place) for this contact/ghost */
  encounterPlace?: { place?: string; city?: string; country?: string; state?: string; region?: string }
  /** Cadre de la rencontre (meeting context) ids */
  meetingContextIds?: string[]
  /** Émotion associée (emotional context) ids */
  emotionalContextIds?: string[]
  /** Évolution de la relation (relationship status) id */
  relationshipStatusId?: string | null
  /** ISO date of the last message exchanged with this contact */
  lastInteractionAt?: string | null
}

interface GetProfileResponse {
  profile: Profile
}

const fetchProfile = async (lang: string): Promise<Profile> => {
  const { data } = await api.get<GetProfileResponse>('/profile/me', {
    params: { lang },
  })
  return data.profile
}

export const useGetProfile = () => {
  const { currentLanguage } = useLanguage()
  const query = useQuery<Profile>({
    queryKey: ['profile', 'me', currentLanguage],
    queryFn: () => fetchProfile(currentLanguage),
  })

  const switchLanguage = useLanguageStore((s) => s.switchLanguage)

  // Sync app language from profile.language only while the user keeps "auto" in settings.
  // After we apply the account language we store an explicit 'en'|'fr'|'es', so we stop syncing.
  // Otherwise (e.g. API echoes request `lang` in profile.language) placeholder/stale data can
  // disagree with the store and ping-pong switchLanguage → maximum update depth exceeded.
  useEffect(() => {
    if (!query.isSuccess || query.isFetching || !query.data?.language) return

    const language = useLanguageStore.getState().language
    if (language !== 'auto') return

    const backendLang = query.data.language as AvailableLanguages
    if (!['en', 'fr', 'es'].includes(backendLang)) return

    const resolvedLocal = resolveLanguage(language)
    if (resolvedLocal === backendLang) return

    switchLanguage(backendLang)
  }, [query.isSuccess, query.isFetching, query.data?.language, switchLanguage])

  return query
}

